import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Check,
  Compass,
  GraduationCap,
  ListChecks,
} from 'lucide-react';
import { auth } from '../firebase';
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import MeltPanel from '../components/MeltPanel';
import MultiSelect from '../components/onboarding/MultiSelect';
import OnboardingPreviews, { BUILD_CARD_COUNT } from '../components/onboarding/OnboardingPreviews';
import {
  CAREER_STAGES,
  Chips,
  DISCOVERY_CHANNELS,
  EducationFields,
  JOB_TYPES,
  LOCATION_GROUPS,
  MeltSelect,
  ROLE_GROUPS,
  useLocationSearchGroups,
} from '../components/onboarding/fields';
import { EMPTY_EDUCATION, type Education, educationDoc } from '../utils/education';
import { cn } from '../lib/utils';
import { MELT_LABEL, MELT_REQUIRED } from '../components/ui/melt';
import { syncGmail } from '../utils/gmailSync';
import { fetchNewRoles, targetsKey } from '../utils/newRolesApi';

// Intro flow shown after a new user signs in (design_handoff_intro_flow).
// Welcome → what you're looking for → about you → setting up.

const PILLARS = [
  { icon: Briefcase, word: 'Track', phrase: 'every application by stage' },
  { icon: Compass, word: 'Discover', phrase: 'roles picked for you' },
  { icon: GraduationCap, word: 'Prepare', phrase: 'for every interview' },
  { icon: ListChecks, word: 'Plan', phrase: 'your next steps' },
];

const TOTAL_STEPS = 4;
const SETUP_STEP = 3;

// Shared field style
const LABEL = MELT_LABEL;

type CheckState = 'idle' | 'run' | 'ok';
type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(auth.currentUser);
  const [step, setStep] = useState(0);

  // Answers
  const [roles, setRoles] = useState<string[]>([]);
  const [jobTypes, setJobTypes] = useState<string[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [careerStage, setCareerStage] = useState<string | null>(null);
  const [education, setEducation] = useState<Education[]>([EMPTY_EDUCATION]);
  const [referralSource, setReferralSource] = useState('');

  // Setup step
  const [checks, setChecks] = useState<CheckState[]>([]);
  const [buildShown, setBuildShown] = useState(0);
  const [setupAnimDone, setSetupAnimDone] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>('idle');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => setUser(currentUser));
    return () => unsubscribe();
  }, []);

  // Onboarding is for new users: anyone who already finished it goes Home. Checked
  // once, so the save at the setup step doesn't bounce them off mid-animation.
  const onboardingChecked = useRef(false);
  useEffect(() => {
    if (!user || onboardingChecked.current) return;
    onboardingChecked.current = true;
    getDoc(doc(getFirestore(), 'users', user.uid))
      .then((snap) => {
        if (snap.data()?.hasCompletedOnboarding) navigate('/dashboard/home', { replace: true });
      })
      .catch((error) => console.error('Could not check onboarding status:', error));
  }, [user, navigate]);

  // Read their Gmail while they answer: the first sync takes a minute or two,
  // about as long as this flow, so Home has their applications when they land.
  // It keeps running when they move on to Home (one sync at a time, utils/gmailSync).
  const syncStarted = useRef(false);
  useEffect(() => {
    if (!user || syncStarted.current) return;
    syncStarted.current = true;
    syncGmail(user).then((result) => {
      if (!result.ok) console.warn('First Gmail sync did not finish during onboarding:', result.error);
    });
  }, [user]);

  const locationSearchGroups = useLocationSearchGroups();

  const displayName = user?.displayName || user?.email || 'there';
  const schoolName = education[0].school.trim();
  const firstName = displayName.split(' ')[0];

  const setupItems = [
    'Setting up your tracker',
    'Planning your first to-dos',
    'Finding roles that match',
    `Building ${roles[0] || 'your'} interview prep`,
  ];

  // New roles start loading as soon as they've picked what they're looking for,
  // so Home has them when they land (the backend matches on the saved users doc,
  // and the result is cached for Home: utils/newRolesApi). The first load also
  // wakes the backend; the one after the last step only adds level and degree.
  const loadRoles = (fields: Record<string, unknown>) => {
    fetchNewRoles('new', targetsKey(fields)).catch((error) =>
      console.warn('New roles did not load during onboarding:', error)
    );
  };

  const saveSearch = async () => {
    if (!user) return;
    const fields = { roles, jobTypes, locations };
    try {
      await setDoc(doc(getFirestore(), 'users', user.uid), fields, { merge: true });
      loadRoles(fields);
    } catch (error) {
      console.warn('Could not save the job search early:', error);
    }
  };

  const saveOnboarding = async () => {
    if (!user) return;
    setSaveState('saving');
    const answers = { roles, jobTypes, locations, careerStage, ...educationDoc(education) };
    try {
      const db = getFirestore();
      await setDoc(
        doc(db, 'users', user.uid),
        {
          email: user.email,
          displayName: user.displayName,
          ...answers,
          referralSource: referralSource || null,
          hasCompletedOnboarding: true,
          // Home: show what the first sync found, once; and the setup to-dos
          firstSyncReveal: true,
          starterTodos: true,
          exploreTodos: true,
          onboardingCompletedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          // No plan fields: only the backend writes those (backend/plans.py)
        },
        { merge: true }
      );
      setSaveState('saved');
      loadRoles(answers);
    } catch (error) {
      console.error('Error saving onboarding data:', error);
      setSaveState('error');
    }
  };

  // Entering the setup step: save answers and play the checklist + board fill-in.
  useEffect(() => {
    if (step !== SETUP_STEP) return;
    saveOnboarding();

    setChecks(setupItems.map(() => 'idle'));
    setBuildShown(0);
    setSetupAnimDone(false);

    const timers: number[] = [];
    const at = (ms: number, fn: () => void) => timers.push(window.setTimeout(fn, ms));
    setupItems.forEach((_, i) => {
      at(i * 1400, () => setChecks((c) => c.map((s, j) => (j === i ? 'run' : s))));
      at(i * 1400 + 1200, () => setChecks((c) => c.map((s, j) => (j === i ? 'ok' : s))));
    });
    for (let i = 0; i < BUILD_CARD_COUNT; i++) at(600 + i * 260, () => setBuildShown(i + 1));
    at(5800, () => setSetupAnimDone(true));

    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const setupDone = setupAnimDone && saveState === 'saved';

  const toggleIn = (list: string[], set: (v: string[]) => void) => (value: string) =>
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const canContinue =
    step === 1 ? roles.length > 0 && jobTypes.length > 0 && locations.length > 0 : step === 2 ? Boolean(careerStage) : true;

  const nextLabel =
    step === 0 ? 'Get started' : step === SETUP_STEP ? (saveState === 'error' ? 'Try again' : 'Open Caramel') : 'Continue';

  const onNext = () => {
    if (step === SETUP_STEP) {
      if (saveState === 'error') saveOnboarding();
      else navigate('/dashboard/home');
      return;
    }
    if (step === 1) saveSearch();
    setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  };

  const showNext = step !== SETUP_STEP || setupDone || saveState === 'error';

  return (
    // Desktop: the page fills the window and only the question side scrolls, so
    // dropdowns and longer steps never shift the page. Narrow: one scrolling page.
    <main className="grid h-screen grid-cols-[1.05fr_1fr] overflow-hidden bg-melt-paper font-grotesk text-melt-text1 antialiased max-[980px]:h-auto max-[980px]:min-h-screen max-[980px]:grid-cols-1 max-[980px]:overflow-visible">
      <MeltPanel className="max-[980px]:min-h-[540px]">
        <OnboardingPreviews
          step={step}
          buildShown={buildShown}
          answers={{ roles, jobTypes, locations, careerStage, school: schoolName }}
        />
      </MeltPanel>

      <section className="scrollbar-thin flex min-w-0 flex-col overflow-y-auto px-14 pb-8 pt-10 max-[980px]:overflow-visible max-[980px]:px-6 max-[980px]:py-8">
        {/* Progress */}
        <div className="flex items-center gap-1.5" aria-label={`Step ${step + 1} of ${TOTAL_STEPS}`}>
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <i
              key={i}
              className={cn(
                'h-1 w-[22px] rounded bg-melt-track transition-[background-color,width] duration-300',
                i < step && 'bg-melt-ginger',
                i === step && 'w-[34px] bg-melt-caramel'
              )}
            />
          ))}
        </div>

        {/* Step content */}
        <div className="flex max-w-[480px] flex-1 flex-col justify-center py-8">
          <div key={step} className="flex flex-col gap-6 duration-[400ms] animate-in fade-in slide-in-from-bottom-2">
            {step === 0 && (
              <>
                <h1 className="text-[38px] font-semibold leading-[1.04] tracking-[-.035em] [text-wrap:balance]">
                  Welcome to Caramel, {firstName}.
                </h1>
                <p className="-mt-2.5 text-base leading-[1.55] text-melt-text2">
                  Caramel connects every part of your job hunt in one place and automates the work in between. A few
                  quick questions and we'll set it up for you.
                </p>
                <div className="flex flex-col">
                  {PILLARS.map(({ icon: Icon, word, phrase }) => (
                    <div
                      key={word}
                      className="flex items-center gap-3.5 border-t border-melt-hair py-[11px] text-sm text-melt-text2 last:border-b"
                    >
                      <span className="grid h-[30px] w-[30px] flex-none place-items-center rounded-[9px] bg-melt-peach text-melt-amber">
                        <Icon size={17} strokeWidth={1.75} />
                      </span>
                      <span>
                        <b className="mr-1 font-semibold text-melt-text1">{word}</b>
                        {phrase}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <h1 className="text-[38px] font-semibold leading-[1.04] tracking-[-.035em] [text-wrap:balance]">
                  What are you looking for?
                </h1>
                <p className="-mt-2.5 text-base leading-[1.55] text-melt-text2">
                  Pick as many as you like. We'll find the roles, companies and prep that match.
                </p>
                <div className="flex flex-col gap-[9px]">
                  <label htmlFor="ob-roles" className={LABEL}>Roles<span aria-hidden className={MELT_REQUIRED}>*</span></label>
                  <MultiSelect id="ob-roles" groups={ROLE_GROUPS} value={roles} onChange={setRoles} placeholder="Search or type a role, e.g. Mechanical" allowCustom />
                </div>
                <div className="flex flex-col gap-[9px]">
                  <span className={LABEL}>Type<span aria-hidden className={MELT_REQUIRED}>*</span></span>
                  <Chips options={JOB_TYPES} isSelected={(o) => jobTypes.includes(o)} onToggle={toggleIn(jobTypes, setJobTypes)} />
                </div>
                <div className="flex flex-col gap-[9px]">
                  <label htmlFor="ob-locations" className={LABEL}>
                    Locations<span aria-hidden className={MELT_REQUIRED}>*</span>
                  </label>
                  <MultiSelect
                    id="ob-locations"
                    groups={LOCATION_GROUPS}
                    searchGroups={locationSearchGroups}
                    allowCustom
                    value={locations}
                    onChange={setLocations}
                    placeholder="Cities, countries, regions or Remote"
                  />
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <h1 className="text-[38px] font-semibold leading-[1.04] tracking-[-.035em] [text-wrap:balance]">
                  A little about you.
                </h1>
                <p className="-mt-2.5 text-base leading-[1.55] text-melt-text2">
                  This helps us match roles to your level.
                </p>
                <div className="flex flex-col gap-[9px]">
                  <span className={LABEL}>Where are you in your career?<span aria-hidden className={MELT_REQUIRED}>*</span></span>
                  <Chips options={CAREER_STAGES} isSelected={(o) => careerStage === o} onToggle={setCareerStage} />
                </div>

                <EducationFields id="ob" value={education} onChange={setEducation} labelClassName={LABEL} />

                <div className="flex flex-col gap-[9px]">
                  <span className={LABEL}>
                    How did you hear about us?
                  </span>
                  <MeltSelect value={referralSource} onChange={setReferralSource} placeholder="Choose one" options={DISCOVERY_CHANNELS} />
                </div>
              </>
            )}

            {step === SETUP_STEP && (
              <>
                <h1 className="text-[38px] font-semibold leading-[1.04] tracking-[-.035em] [text-wrap:balance]">
                  {setupDone ? 'You’re all set.' : 'Setting up your workspace.'}
                </h1>
                <p className="-mt-2.5 text-base leading-[1.55] text-melt-text2">
                  {saveState === 'error' ? "We couldn't save your answers. Please try again." : 'This takes about a minute.'}
                </p>
                <div className="flex flex-col gap-3">
                  {setupItems.map((item, i) => {
                    const state = checks[i] ?? 'idle';
                    return (
                      <div
                        key={i}
                        className={cn(
                          'flex items-center gap-3 text-[15px] text-melt-text3 transition-colors duration-300',
                          state !== 'idle' && 'text-melt-text1'
                        )}
                      >
                        <span
                          className={cn(
                            'grid h-[22px] w-[22px] place-items-center rounded-full border-[1.5px] border-melt-text1/20 transition-all duration-300',
                            state === 'run' && 'animate-[spin_.8s_linear_infinite] border-melt-caramel border-t-transparent',
                            state === 'ok' && 'border-melt-caramel bg-melt-caramel'
                          )}
                        >
                          <Check size={13} className={cn('text-white', state !== 'ok' && 'opacity-0')} />
                        </span>
                        {item}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Nav */}
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(s - 1, 0))}
            className={cn(
              'inline-flex h-12 items-center gap-2 px-1.5 text-[15px] font-medium text-melt-text2 hover:text-melt-text1',
              (step === 0 || step === SETUP_STEP) && 'invisible'
            )}
          >
            <ArrowLeft size={16} strokeWidth={1.75} />
            Back
          </button>
          <span className="font-plex text-xs leading-none text-melt-text3">
            {step + 1} of {TOTAL_STEPS}
          </span>
          <span className="flex items-center gap-3.5">
            <button
              type="button"
              onClick={onNext}
              disabled={!canContinue}
              className={cn(
                'inline-flex h-12 items-center gap-2 rounded-xl bg-melt-caramel px-[22px] text-[15px] font-medium text-white transition-[background-color,opacity] duration-150 hover:bg-melt-press disabled:cursor-default disabled:opacity-45 disabled:hover:bg-melt-caramel',
                !showNext && 'hidden'
              )}
            >
              {nextLabel}
              <ArrowRight size={16} strokeWidth={1.75} />
            </button>
          </span>
        </div>
      </section>
    </main>
  );
};

export default Onboarding;
